/**
 * 個別説明「実施状況」タブの行データ組み立て (2026-10-07)。
 *
 * 1 行 = 個別説明 1 件。商談ステージに依存せず、次の 3 種類から行を作る:
 *   - 確定済みセッション (dealBriefingSessions)。記録があれば記録と対にする
 *   - セッションと対にならなかった記録 (商談画面からの手動追加など)
 *   - 未処理の申込 (pending 候補を申込 = formSubmissionId ごとにまとめる)。
 *     category='explanation' の商談で行が 1 つも無い場合も「日程未確定」1 行を出す
 *
 * 記録とセッションの対応:
 *   記録の briefingScheduleId がセッションの scheduleId と一致すれば対にする。
 *   記録ページからの保存は briefingScheduleId='unknown' で保存されるため一致しないことが多く、
 *   残りは「予約日の古いセッション」と「記録日の古い記録」を順に対にする。
 *
 * 削除済み商談 (合体元を含む) は記録の行だけを出す。記録は合体先へ移らず、
 * SA 請求書にも含まれうるため (functions/src/commissions/briefingFees.ts)。
 *
 * 仕様書: docs/01_spec/sales_implementation_prompt_v3_1.md §7.9
 */
import type { CommissionInvoice } from '@goenhub/shared';
import { dealBriefingCandidates, dealBriefingSessions } from '@goenhub/shared';

import type { BriefingRecordWithDeal } from '@/hooks/useBriefings';
import type { DealRecord } from '@/hooks/useDeals';
import { toDate } from '@/lib/firestoreTime';

export type BriefingRowState =
  | 'completed'
  | 'draft'
  | 'not_conducted'
  | 'no_record'
  | 'unscheduled';

export const BRIEFING_ROW_STATE_LABEL: Record<BriefingRowState, string> = {
  completed: '実施済み',
  draft: '下書き',
  not_conducted: '未実施',
  no_record: '記録なし',
  unscheduled: '日程未確定',
};

export type BriefingBillingKind = 'unbilled' | 'billed' | 'paid' | 'not_applicable' | 'none';

export const BRIEFING_BILLING_LABEL: Record<Exclude<BriefingBillingKind, 'none'>, string> = {
  unbilled: '未請求',
  billed: '請求済',
  paid: '支払済',
  not_applicable: '対象外',
};

/** 対象外の理由。 */
export type NotApplicableReason = '未実施' | '下書き' | '社内' | '代行料の設定なし';

export interface BriefingBilling {
  kind: BriefingBillingKind;
  reason?: NotApplicableReason;
  invoiceId?: string;
  invoiceNumber?: string;
}

export interface BriefingStatusRow {
  /** 行の一意キー (戻った時の行の特定に使う) */
  key: string;
  dealId: string;
  dealDeleted: boolean;
  contactId: string | null;
  dealTitle: string;
  appliedAt: Date | null;
  scheduledAt: Date | null;
  /** 記録を最初に保存した日時 (未実施は未実施にした日時) */
  recordedAt: Date | null;
  saUid: string | null;
  state: BriefingRowState;
  billing: BriefingBilling;
  /** 'YYYY-MM'。予約日 → 申込日 → 記録日 の順で決める */
  monthKey: string;
}

/** SA 請求の判定に使う請求書 (id 付き)。 */
export type InvoiceLike = Pick<
  CommissionInvoice,
  | 'invoiceNumber'
  | 'status'
  | 'recipientId'
  | 'recipientName'
  | 'lineItems'
  | 'issuedAt'
  | 'paidAt'
  | 'publicToken'
> & { id: string };

export interface BuildRowsInput {
  /** 削除済みを含む全商談 */
  deals: DealRecord[];
  records: BriefingRecordWithDeal[];
  invoices: InvoiceLike[];
  /** uid → 外部SAか (level='external')。代行料なしの理由 (社内 / 設定なし) の判定に使う */
  isExternal: (uid: string) => boolean;
  /** uid → 有効な報酬セットの個別説明代行料 (未設定は 0) */
  briefingFeeOf: (uid: string) => number;
}

function monthKeyOf(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function minDate(values: unknown[]): Date | null {
  let out: Date | null = null;
  for (const v of values) {
    const d = toDate(v);
    if (d && (!out || d < out)) out = d;
  }
  return out;
}

function recordState(r: BriefingRecordWithDeal): BriefingRowState {
  const s = r.status ?? 'completed';
  if (s === 'draft') return 'draft';
  if (s === 'not_conducted') return 'not_conducted';
  return 'completed';
}

function billingOf(
  r: BriefingRecordWithDeal | null,
  invoiceById: Map<string, InvoiceLike>,
  input: BuildRowsInput,
): BriefingBilling {
  if (!r) return { kind: 'none' };
  const state = recordState(r);
  if (state === 'not_conducted') return { kind: 'not_applicable', reason: '未実施' };
  if (state === 'draft') return { kind: 'not_applicable', reason: '下書き' };
  const inv = r.commissionInvoiceId ? invoiceById.get(r.commissionInvoiceId) : undefined;
  if (inv && inv.status !== 'cancelled') {
    const kind: BriefingBillingKind = inv.status === 'paid' ? 'paid' : 'billed';
    return { kind, invoiceId: inv.id, invoiceNumber: inv.invoiceNumber };
  }
  // 請求できるかは代行料単価で決まる (collectUnbilledBriefings / listMyUnbilledBriefings と同じ)。
  // 単価なしのうち外部SA以外を「社内」とする
  if (input.briefingFeeOf(r.conductedBy) <= 0) {
    return {
      kind: 'not_applicable',
      reason: input.isExternal(r.conductedBy) ? '代行料の設定なし' : '社内',
    };
  }
  return { kind: 'unbilled' };
}

export function buildBriefingStatusRows(input: BuildRowsInput): BriefingStatusRow[] {
  const invoiceById = new Map(input.invoices.map((i) => [i.id, i]));
  const recordsByDeal = new Map<string, BriefingRecordWithDeal[]>();
  for (const r of input.records) {
    const list = recordsByDeal.get(r.dealId) ?? [];
    list.push(r);
    recordsByDeal.set(r.dealId, list);
  }

  const rows: BriefingStatusRow[] = [];
  const push = (
    deal: DealRecord,
    key: string,
    args: {
      appliedAt: Date | null;
      scheduledAt: Date | null;
      record: BriefingRecordWithDeal | null;
      saUid: string | null;
      state: BriefingRowState;
    },
  ) => {
    const recordedAt = args.record ? toDate(args.record.conductedAt) : null;
    const basis = args.scheduledAt ?? args.appliedAt ?? recordedAt;
    rows.push({
      key,
      dealId: deal.id,
      dealDeleted: !!deal._deleted,
      contactId: deal.contactId ?? null,
      dealTitle: deal.title,
      appliedAt: args.appliedAt,
      scheduledAt: args.scheduledAt,
      recordedAt,
      saUid: args.saUid,
      state: args.state,
      billing: billingOf(args.record, invoiceById, input),
      monthKey: basis ? monthKeyOf(basis) : '',
    });
  };

  for (const deal of input.deals) {
    const records = [...(recordsByDeal.get(deal.id) ?? [])].sort(
      (a, b) => (toDate(a.conductedAt)?.getTime() ?? 0) - (toDate(b.conductedAt)?.getTime() ?? 0),
    );

    // 削除済み商談: 記録の行だけ
    if (deal._deleted) {
      for (const r of records) {
        push(deal, `${deal.id}:r:${r.id}`, {
          appliedAt: null,
          scheduledAt: null,
          record: r,
          saUid: r.conductedBy ?? null,
          state: recordState(r),
        });
      }
      continue;
    }

    const candidates = dealBriefingCandidates(deal);
    const sessions = [...dealBriefingSessions(deal)].sort(
      (a, b) => (toDate(a.scheduledAt)?.getTime() ?? 0) - (toDate(b.scheduledAt)?.getTime() ?? 0),
    );
    const pending = candidates.filter((c) => c.status === 'pending');
    const isExplanation = deal.category === 'explanation';
    if (
      !isExplanation &&
      sessions.length === 0 &&
      candidates.length === 0 &&
      records.length === 0
    ) {
      continue;
    }

    // ── セッションと記録の対応付け ──
    const pairOf = new Map<number, BriefingRecordWithDeal>();
    const used = new Set<string>();
    sessions.forEach((s, i) => {
      if (!s.scheduleId) return;
      const r = records.find((x) => !used.has(x.id) && x.briefingScheduleId === s.scheduleId);
      if (r) {
        pairOf.set(i, r);
        used.add(r.id);
      }
    });
    const leftover = records.filter((r) => !used.has(r.id));
    sessions.forEach((_s, i) => {
      if (pairOf.has(i)) return;
      const r = leftover.shift();
      if (r) {
        pairOf.set(i, r);
        used.add(r.id);
      }
    });

    // ── セッションの行 ──
    sessions.forEach((s, i) => {
      const r = pairOf.get(i) ?? null;
      const fromSameApplication = s.formSubmissionId
        ? candidates.filter((c) => c.formSubmissionId === s.formSubmissionId)
        : candidates.filter((c) => c.status === 'confirmed' && c.scheduleId === s.scheduleId);
      let appliedAt = minDate(fromSameApplication.map((c) => c.createdAt));
      // 候補を持たない旧データ: フォーム送信で作られた商談の作成日時 = 申込日
      if (!appliedAt && isExplanation && i === 0) appliedAt = toDate(deal.createdAt);
      const state = r ? recordState(r) : 'no_record';
      // 実施担当は記録の保存者 (請求の判定と同じ)。未実施マークの保存者はマーク実行者なので枠の担当を使う
      const saUid =
        r && state !== 'not_conducted' ? r.conductedBy : (s.assigneeUid ?? r?.conductedBy ?? null);
      push(deal, `${deal.id}:s:${i}`, {
        appliedAt,
        scheduledAt: toDate(s.scheduledAt),
        record: r,
        saUid,
        state,
      });
    });

    // ── セッションと対にならなかった記録の行 ──
    for (const r of leftover) {
      push(deal, `${deal.id}:r:${r.id}`, {
        appliedAt: sessions.length === 0 && isExplanation ? toDate(deal.createdAt) : null,
        scheduledAt: null,
        record: r,
        saUid: r.conductedBy ?? null,
        state: recordState(r),
      });
    }

    // ── 未処理の申込 (日程未確定) ──
    const groups = new Map<string, typeof pending>();
    for (const c of pending) {
      const k = c.formSubmissionId ?? '_';
      groups.set(k, [...(groups.get(k) ?? []), c]);
    }
    for (const [k, cs] of groups) {
      push(deal, `${deal.id}:p:${k}`, {
        appliedAt: minDate(cs.map((c) => c.createdAt)) ?? toDate(deal.createdAt),
        scheduledAt: null,
        record: null,
        saUid: null,
        state: 'unscheduled',
      });
    }

    // 申込はあるが候補・日程・記録のどれも無い (候補が辞退済みのみ等)
    if (isExplanation && sessions.length === 0 && records.length === 0 && pending.length === 0) {
      push(deal, `${deal.id}:p:_`, {
        appliedAt: minDate(candidates.map((c) => c.createdAt)) ?? toDate(deal.createdAt),
        scheduledAt: null,
        record: null,
        saUid: null,
        state: 'unscheduled',
      });
    }
  }

  const sortAt = (r: BriefingStatusRow) =>
    (r.scheduledAt ?? r.appliedAt ?? r.recordedAt)?.getTime() ?? 0;
  return rows.sort((a, b) => sortAt(a) - sortAt(b));
}

/** 請求書の個別説明代行の明細 (件数・単価・小計)。 */
export function invoiceBriefingSummary(inv: InvoiceLike): {
  quantity: number;
  unitPrice: number | null;
  amount: number;
} {
  let quantity = 0;
  let amount = 0;
  let unitPrice: number | null = null;
  for (const li of inv.lineItems) {
    if (li.kind !== 'briefing_fee') continue;
    quantity += li.quantity ?? 0;
    amount += li.amount;
    if (unitPrice === null && typeof li.unitPrice === 'number') unitPrice = li.unitPrice;
  }
  return { quantity, unitPrice, amount };
}
